import React, { useState } from 'react';
import classNames from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import {
  FormFieldBase,
  FormFieldInput,
  getMapFromArray,
  getFieldPropertyValues,
  fieldIsType,
} from './';
import { replaceAt } from './helpers';
import { transformDisplay, cleanValue } from './form-field-ssn';

const options = ['', 'Male', 'Female'];

const FormFieldResponsibleParty = ({
  field,
  className,
  commonProps,
  isRenderedField,
  onChange,
  value,
  onBlur,
  readOnly,
  relationshipTypes,
}) => {
  const [ssnValue, setSsnValue] = useState('');
  const handleSsnChange = (e) => {
    const target = e.target;
    const cleanedValue = cleanValue(target.value);
    let dataValue = ssnValue;

    for (let i = 0; i < cleanedValue.length; i++) {
      if (/[0-9]/g.exec(cleanedValue[i])) {
        dataValue = replaceAt(dataValue, i, cleanedValue[i]);
      }
    }
    dataValue = dataValue.substr(0, cleanedValue.length);
    setSsnValue(dataValue);
    e.target.value = dataValue;

    onChange(e);
  };

  const getFieldSelectOptions = field => {
    if (!isRenderedField) return;

    if (fieldIsType(field, 'Sex', 'display_name')) {
      return options.map(val => <option key={val}>{val}</option>);
    }

    if (fieldIsType(field, 'Relationship Type', 'display_name')) {
      // splice in a blank option as the first element
      const blankOption = { externalId: '', name: '' };
      const relTypeOptions = relationshipTypes.toSpliced(0, 0, blankOption);
      
      if (relTypeOptions.length === 1) {
        // only our blank element, so add an "Other" option
        relTypeOptions.push({ externalId: 'other', name: 'Other' });
      }

      return relTypeOptions.map(relType => {
        const { externalId, name } = relType;
        return (
          <option key={externalId} value={externalId}>
            {name}
          </option>
        );
      });
    }
  };

  const getFieldOptions = (field, properties, propertyValues) => {
    const fieldOptions = {
      properties,
      sublabel: propertyValues.sublabel,
      subfield: true,
      isRenderedField,
      onChange,
      value: field.value || '',
      onBlur: (e) => onBlur(e, false, true),
      readOnly,
      validationMessage: field.validationMessage,
    };

    if (fieldIsType(field, 'SSN', 'display_name')) {
      const clonedProperties = cloneDeep(properties);
      const placeholder = clonedProperties.get('placeholder');
      placeholder.value = 'xxx-xx-xxxx';
      clonedProperties.set('placeholder', placeholder);
      fieldOptions.properties = clonedProperties;
      fieldOptions.maxLength = 11;
      fieldOptions.value = field.value ? transformDisplay(field.value) : '';
      fieldOptions.onChange = handleSsnChange;
    }

    if (fieldIsType(field, 'Sex', 'display_name')) {
      fieldOptions.onBlur = undefined;
    }

    if (commonProps.makeRequired 
    && (fieldIsType(field, 'First Name', 'display_name') 
    || fieldIsType(field, 'Last Name', 'display_name')
    || fieldIsType(field, 'Relationship Type', 'display_name'))) {
      fieldOptions.sublabel = `${propertyValues.sublabel} *`;
      fieldOptions.onBlur = (e) => onBlur(e, true);
    }

    return fieldOptions;
  };

  const { fields } = field;
  const fieldMap = new Map();

  // transform fields array to:
  // {0: [fields[0], [fields[1]], // first, last
  //  1: [fields[2], fields[3], // relationship type, email
  //  2: [fields[4], fields[5], fields[6]]} // phone, ssn, gender
  fields
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((sf, index) => {
      const subfield = { ...sf };
      let currentIdx = 0;
      if (isRenderedField && value[index] !== undefined) subfield.value = value[index];
      if (index < 2) {
        currentIdx = 1;
      } else if (index >= 2 && index <= 3) {
        currentIdx = 2;
      } else {
        currentIdx = 3;
      }
      if (!fieldMap.get(currentIdx)) fieldMap.set(currentIdx, [subfield]);
      else fieldMap.get(currentIdx).push(subfield);
    });

  const classes = classNames({
    [className]: true,
    'responsible-party': true,
  });

  return (
    <div key={field.uuid || commonProps.label} className={classes}>
      <div className='form-field-label header'>
        {`${commonProps.label} ${commonProps.makeRequired ? '*' : ''}`}
        {!commonProps.makeRequired && (
          <label className='form-field-label secondary'>If the patient is the responsible party, please disregard this section</label>
        )}
      </div>
      {Array.from(fieldMap).map((fields, key) => {
        return (
          <div key={key} className='flex'>
            {fields[1].map(el => {
              const properties = getMapFromArray(el.properties);
              const propertyNames = ['sublabel', 'isVisible'];
              const defaultValues = { sublabel: '', isVisible: true };
              const propertyValues = getFieldPropertyValues(
                properties,
                propertyNames,
                defaultValues,
              );

              if (propertyValues.isVisible) {
                return (
                  <FormFieldInput
                    key={el.id}
                    field={el}
                    children={getFieldSelectOptions(el)}
                    options={getFieldOptions(el, properties, propertyValues)}
                  />
                );
              }
            })}
          </div>
        );
      })}
    </div>
  );
};

export default FormFieldBase(FormFieldResponsibleParty);
